import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import SupplierService from "../../services/SupplierService";
import { FilterMatchMode } from "primereact/api";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { exportToExcel } from "../../utils/excelExportHelper";

export default function SuppliersListPage() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const toast = useRef(null);
  const navigate = useNavigate();

  const handleExport = () => {
    const headers = [
      { label: "Tedarikçi Adı", key: "name" },
      { label: "Telefon", key: "phoneNumber" },
      { label: "E-posta", key: "email" },
      { label: "TCKN / Vergi No", key: "taxNumber" },
      { label: "Vergi Dairesi", key: "taxOffice" },
      { label: "Bakiye (TL)", key: "balanceFormatted" },
      { label: "Banka Bilgisi", key: "bankInfo" }
    ];

    const dataToExport = suppliers.map(s => ({
      ...s,
      balanceFormatted: (s.balance || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })
    }));

    exportToExcel(dataToExport, headers, "tedarikci_listesi");
  };

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const res = await SupplierService.getAllSuppliers();
      setSuppliers(res);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Hata",
        detail: "Tedarikçiler yüklenemedi!",
      });
    } finally {
      setLoading(false);
    }
  };

  const actionsTemplate = (rowData) => (
    <div className="flex gap-1.5 justify-center">
      <Button
        icon="pi pi-eye"
        className="p-button-rounded p-button-text p-button-info hover:bg-blue-50 w-8 h-8 p-0"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/suppliers/detail/${rowData.id}`);
        }}
        tooltip="Detay Gör"
      />
      <Button
        icon="pi pi-pencil"
        className="p-button-rounded p-button-text p-button-warning hover:bg-amber-50 w-8 h-8 p-0"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/suppliers/edit/${rowData.id}`);
        }}
        tooltip="Düzenle"
      />
    </div>
  );

  const balanceTemplate = (row) => {
    const val = row.balance || 0;
    const formatted = val.toLocaleString("tr-TR", {
      style: "currency",
      currency: "TRY"
    });

    return (
      <span className={`font-bold ${val > 0 ? "text-red-600" : val < 0 ? "text-emerald-600" : "text-slate-400"}`}>
        {formatted}
      </span>
    );
  };

  const alacakToplam = suppliers.reduce((sum, s) => (s.balance || 0) < 0 ? sum + Math.abs(s.balance) : sum, 0);
  const verecekToplam = suppliers.reduce((sum, s) => (s.balance || 0) > 0 ? sum + s.balance : sum, 0);
  const netToplam = alacakToplam - verecekToplam;

  const formatCurrency = (value) => {
    return (value ?? 0).toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " ₺";
  };

  const savedSearch = localStorage.getItem("supplier_search_query") || "";
  const [filters, setFilters] = useState({
    global: { value: savedSearch || null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    taxNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    phoneNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    email: { value: null, matchMode: FilterMatchMode.CONTAINS },
    recordStatus: { value: null, matchMode: FilterMatchMode.EQUALS },
    useDueTracking: { value: null, matchMode: FilterMatchMode.EQUALS },
  });

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;
    setFilters(_filters);
    localStorage.setItem("supplier_search_query", value);
  };

  const renderHeader = () => {
    return (
      <div className="flex justify-between items-center bg-slate-50 border-b border-slate-200">
        <span className="p-input-icon-left w-full md:w-80">
          <i className="pi pi-search text-slate-400 ml-3" />
          <InputText
            value={filters["global"].value || ""}
            onChange={onGlobalFilterChange}
            placeholder="Hızlı tedarikçi ara..."
            className="w-full rounded-xl border-slate-200 text-sm py-2 pl-10"
          />
        </span>
        <div className="flex gap-2">
          <Button
            type="button"
            icon="pi pi-filter-slash"
            label="Temizle"
            className="p-button-outlined p-button-secondary p-button-sm rounded-xl ml-3"
            onClick={() => {
              setFilters({
                global: { value: null, matchMode: FilterMatchMode.CONTAINS },
                name: { value: null, matchMode: FilterMatchMode.CONTAINS },
                taxNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
                phoneNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
                email: { value: null, matchMode: FilterMatchMode.CONTAINS },
                recordStatus: { value: null, matchMode: FilterMatchMode.EQUALS },
                useDueTracking: { value: null, matchMode: FilterMatchMode.EQUALS },
              });
              localStorage.removeItem("supplier_search_query");
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="  animate-fade-in  min-h-screen">
      <Toast ref={toast} appendTo={mounted ? document.body : "self"} />

      <div className="!bg-slate-200 dark:!bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden rounded-2xl  ">
        <div className="px-4 py-4 border-b border-slate-200 dark:border-slate-700   flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <div className="!bg-blue-600 p-4 rounded-xl shadow-md shadow-blue-500/20">
              <i className="pi pi-building text-white text-lg"></i>
            </div>
            <div>
              <h1 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Tedarikçi Listesi</h1>
              <p className="text-slate-500 dark:text-slate-400 text-xs font-bold italic">Tedarikçi hesapları ve bakiye yönetimi</p>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button
              label="EXCEL'DEN İÇE AKTAR"
              icon="pi pi-file-excel"
              className="!bg-emerald-600 border-none hover:!bg-emerald-700 px-5 py-2.5 rounded-xl font-black text-xs shadow-lg shadow-emerald-500/20 transition-all active:scale-95"
              onClick={() => navigate("/suppliers/import")}
            />
            <Button
              label="VERİLERİ DIŞARI AKTAR"
              icon="pi pi-file-export"
              className="!bg-slate-700 border-none hover:!bg-slate-800 px-5 py-2.5 rounded-xl font-black text-xs shadow-lg shadow-slate-500/20 transition-all active:scale-95"
              onClick={handleExport}
            />
            <Button
              label="YENİ TEDARİKÇİ"
              icon="pi pi-plus"
              className="!bg-blue-600 border-none hover:!bg-blue-700 rounded-xl px-5 py-2.5 font-black text-sm shadow-lg shadow-blue-500/20 transition-all hover:scale-[1.02]"
              onClick={() => navigate("/suppliers/add")}
            />
          </div>
        </div>
      </div>

      <div className="bg-white shadow-sm border border-slate-200   overflow-hidden">

        {/* Bilgi Kartları */}
        <div className="p-4 bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-4 bg-blue-600 rounded-full"></div> 
            </div>
            
            <button
              type="button"
              onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
              className="
                group
                !w-8 !h-8
                flex-shrink-0
                rounded-xl
                bg-slate-200/60 hover:bg-slate-300/80
                dark:bg-slate-800 dark:hover:bg-slate-700
                text-slate-600 dark:text-slate-300
                hover:text-slate-800 dark:hover:text-white
                active:scale-95
                transition-all duration-300
                flex items-center justify-center border-none
              "
              title={isSummaryExpanded ? "Özeti Gizle" : "Özeti Göster"}
            >
              <i className={`pi pi-chevron-down text-[12px] transition-transform duration-300 ${isSummaryExpanded ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {isSummaryExpanded && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 animate-fade-in">
              <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm flex items-center justify-between">
                <div>
                  <p className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">Alacak Toplamı</p>
                  <h3 className="text-xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight">
                    {formatCurrency(alacakToplam)}
                  </h3>
                </div>
                <div className="w-2 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 flex-shrink-0">
                  <i className="pi pi-arrow-up-right text-sm" />
                </div>
              </div>

              <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm flex items-center justify-between">
                <div>
                  <p className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">Verecek Toplamı</p>
                  <h3 className="text-xl font-black text-rose-600 dark:text-rose-400 tracking-tight">
                    {formatCurrency(verecekToplam)}
                  </h3>
                </div>
                <div className="w-2 h-8 rounded-lg bg-rose-50 dark:bg-rose-950/30 flex items-center justify-center text-rose-600 dark:text-rose-400 flex-shrink-0">
                  <i className="pi pi-arrow-down-left text-sm" />
                </div>
              </div>

              <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm flex items-center justify-between">
                <div>
                  <p className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">Net Toplam</p>
                  <h3 className={`text-xl font-black tracking-tight ${netToplam >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                    {formatCurrency(netToplam)}
                  </h3>
                </div>
                <div className={`w-2 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${netToplam >= 0 ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400" : "bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-400"}`}>
                  <i className="pi pi-calculator text-sm" />
                </div>
              </div>
            </div>
          )}
        </div>

        <DataTable
          value={suppliers}
          loading={loading}
          paginator
          rows={20}
          className="p-datatable-sm"
          filters={filters}
          onFilter={(e) => setFilters(e.filters)}
          filterDisplay="menu"
          globalFilterFields={["name", "taxNumber", "phoneNumber", "email"]}
          rowHover
          showGridlines
          responsiveLayout="scroll"
          emptyMessage="Tedarikçi bulunamadı."
          header={renderHeader()}
          paginatorClassName="border-t border-slate-100 px-4 py-2 bg-white"
          rowClassName={() => 'hover:bg-slate-50 transition-colors cursor-pointer'}
          onRowClick={(e) => navigate(`/suppliers/detail/${e.data.id}`)}
        >
          <Column field="id" header="ID" sortable className="text-slate-400 font-bold text-xs" style={{ width: '80px' }} align="left" />
          <Column
            field="name"
            header="TEDARİKÇİ ADI"
            headerClassName="bg-slate-50 text-slate-500 font-black text-[11px] uppercase py-4 px-6"
            sortable
            filter
            filterPlaceholder="İsim ile ara..."
            className="text-slate-800 font-bold"
            align="left"
          />
          <Column
            field="balance"
            header="BAKİYE"
            body={balanceTemplate}
            sortable
            className="font-black"
            headerClassName="bg-slate-50 text-slate-500 font-black text-[11px] uppercase py-4 px-6"
            align="right"
          />
          <Column
            field="nextDueDate"
            header="VADE TARİHİ"
            headerClassName="bg-slate-50 text-slate-500 font-black text-[11px] uppercase py-4 px-6"
            sortable
            body={(row) => {
              if (!row.nextDueDate) return <span className="text-slate-400 font-bold">-</span>;
              const date = new Date(row.nextDueDate);
              const formatted = date.toLocaleDateString("tr-TR");
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const dueDate = new Date(row.nextDueDate);
              dueDate.setHours(0, 0, 0, 0);
              const isOverdue = dueDate < today;
              return (
                <span className={`font-bold ${isOverdue ? "text-rose-600 bg-rose-50 px-2 py-1 rounded" : "text-slate-700"}`}>
                  {formatted}
                </span>
              );
            }}
            className="font-bold"
            align="center"
          />
          <Column
            header="HESAP / DURUM"
            field="useDueTracking"
            sortable
            filter
            headerClassName="bg-slate-50 text-slate-500 font-black text-[11px] uppercase py-4 px-6"
            align="center"
            filterElement={(options) => (
              <Dropdown
                value={options.value}
                options={[
                  { label: "Vade Takipli", value: true },
                  { label: "Açık Hesap", value: false },
                ]}
                onChange={(e) => options.filterCallback(e.value)}
                placeholder="Seç"
                showClear
                className="w-full rounded-xl border-slate-200"
                appendTo={document.body}
              />
            )}
            body={(row) => {
              const isAktif = row.recordStatus === 1;
              const isVade = row.useDueTracking;
              return (
                <div className="flex items-center justify-center py-0.5">
                  <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-lg border border-slate-200/80 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-800/50 shadow-sm transition-all hover:border-slate-300 dark:hover:border-slate-600">
                    {/* Hesap Tipi */}
                    <span className={`inline-flex items-center gap-1 text-[9px] font-black tracking-wider ${isVade
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-indigo-600 dark:text-indigo-400'
                      }`}>
                      <i className={`pi ${isVade ? 'pi-calendar' : 'pi-wallet'} text-[9px]`} />
                      {isVade ? "VADE TAKİPLİ" : "AÇIK HESAP"}
                    </span>

                    {/* Dikey Ayrışma Çizgisi */}
                    <span className="w-[1px] h-3 bg-slate-200 dark:bg-slate-700" />

                    {/* Durum */}
                    <span className={`inline-flex items-center gap-1.5 text-[9px] font-black tracking-wider ${isAktif
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-rose-600 dark:text-rose-400'
                      }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${isAktif
                        ? 'bg-emerald-500 animate-pulse shadow-[0_0_6px_rgba(16,185,129,0.5)]'
                        : 'bg-rose-500'
                        }`} />
                      {isAktif ? "AKTİF" : "PASİF"}
                    </span>
                  </div>
                </div>
              );
            }}
          />
          <Column
            header="İŞLEMLER"
            body={actionsTemplate}
            style={{ width: "100px" }}
            headerClassName="bg-slate-50 text-slate-500 font-black text-[11px] uppercase py-4 px-6"
            align="center"
          />
        </DataTable>


      </div>
    </div>
  );
}
